import { AppState } from '../../../redux/reducers';

// stores
import * as DebtsStore from '../../../redux/middleware/CashFlow-Debts.Middleware';

// models
import { DebtIndicators } from '../../../models/Entities/CashFlow/Debt/DebtIndicators';
import { DebtSummary } from '../../../models/Entities/CashFlow/Debt/DebtSummary';
import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { ErrorType } from '../../../models/HttpError';

interface IProps {
    UserId: string;
    /**
     * Optional. When provided, indicators are computed client-side from the
     * already-loaded (and filtered) DebtsAsCreditor/DebtsAsDebtor in the store
     * — restricting results to debts between UserId and this related entity.
     * When omitted, the global aggregated indicators endpoint is used.
     */
    RelatedEntityIdentifier?: string;
}

interface ConnectedProps {
    isLoadingIndicators: boolean;
    successLoadingIndicators: boolean | undefined;
    failOnLoadingIndicators: boolean;
    indicators: DebtIndicators | undefined;
    error: ErrorType | undefined;
    useLocalIndicators: boolean;
}

interface DispatchProps {
    GetDebtIndicatorsByUserId: typeof DebtsStore.actionCreators.GetDebtIndicatorsByUserId;
}

export type Props = IProps & ConnectedProps & DispatchProps;

const aggregateByCurrency = (debts: Debt[]): DebtSummary[] => {
    const map = new Map<string, DebtSummary>();
    debts.forEach((d) => {
        if (!d || (d.pendingAmount || 0) <= 0) return;
        const code = d.currencyCode || 'ARS';
        const existing = map.get(code) || {
            currencyCode: code,
            totalPendingAmount: 0,
            totalOriginalAmount: 0,
            totalPaidAmount: 0,
            pendingCount: 0
        };
        existing.totalPendingAmount += d.pendingAmount || 0;
        existing.totalOriginalAmount += d.originalAmount || 0;
        existing.totalPaidAmount += d.paidAmount || 0;
        existing.pendingCount += 1;
        map.set(code, existing);
    });
    return Array.from(map.values());
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const useLocal = !!ownProps.RelatedEntityIdentifier;

    if (useLocal) {
        const debtsAsCreditor: Debt[] = state.CashFlowDebts?.DebtsAsCreditor || [];
        const debtsAsDebtor: Debt[] = state.CashFlowDebts?.DebtsAsDebtor || [];

        const localIndicators: DebtIndicators = {
            pendingToPay: aggregateByCurrency(debtsAsDebtor),
            pendingToCollect: aggregateByCurrency(debtsAsCreditor)
        };

        return {
            isLoadingIndicators: state.CashFlowDebts?.isLoadingAll || false,
            successLoadingIndicators: state.CashFlowDebts?.successLoadingAll,
            failOnLoadingIndicators: state.CashFlowDebts?.failOnLoadingAll || false,
            indicators: localIndicators,
            error: state.CashFlowDebts?.error,
            useLocalIndicators: true
        };
    }

    return {
        isLoadingIndicators: state.CashFlowDebts?.isLoadingIndicators || false,
        successLoadingIndicators: state.CashFlowDebts?.successLoadingIndicators,
        failOnLoadingIndicators: state.CashFlowDebts?.failOnLoadingIndicators || false,
        indicators: state.CashFlowDebts?.debtIndicators,
        error: state.CashFlowDebts?.error,
        useLocalIndicators: false
    };
};

export const mapDispatchToProps = {
    GetDebtIndicatorsByUserId: DebtsStore.actionCreators.GetDebtIndicatorsByUserId
};

